// The ending creature's two persistent-area hooks, in collect_persistent_aoes order.
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import { collectPersistentZones, intersectingZoneKeys, paintedZoneSnapshot, zoneIsPainted, ZoneHandBack, type ZoneReaders } from './zone-membership.js';
import { type JanusCtx, type Row, HandBack, handBack, ecId, itemField, record, upper } from '../ability/context.js';
import { janus } from '../ability/janus.js';
import { PAOE_END } from '../ability/paoe-entry-mode.js';
import { checkOpposingTeam } from '../ability/check-opposing-team.js';
import { removeStatusFromRow, statusItemId, statusRows } from '../statuses/expiry.js';
import contract from '../pl/pl-cost-contract.json';
import { carrierHasTrigger } from '../ability/statuses/status-trigger-gate.js';
import { runTriggerFamily, triggerFamilyPreDraw } from '../ability/trigger-executor.js';
import { statusInputSnapshot } from '../turns/input-snapshots.js';
import { sameTeamOrSelf, ZONE_GATE_SKIP_SAME_TEAM } from '../ability/team-skip-gate.js';
import { classAuraActiveAnchor } from '../statuses/class-aura.js';
import { classAuraExpectedStored, refreshClassAuraZones, type WalkZoneReaders } from './walk-zone-crossings.js';
import { refuseUnported } from '../unported.js';

const adverseNames = new Set(['damage_roll', 'contesting_roll', 'damage_over_time_roll', 'weapon_melee_attack_roll',
  'weapon_ranged_attack_roll', 'spell_melee_attack_roll', 'spell_ranged_attack_roll', 'extra_damage_roll_taken', 'mana_drain_roll']);
const adverseIds = new Set(contract.catalogs.rollTypes.filter(r => adverseNames.has(r.variable_name)).map(r => upper(r.item_id)));
export const zoneRollIsAdverse = (rollType: string): boolean => adverseIds.has(rollType);
const truthy = (v: unknown): boolean => v != null && v !== '' && v !== 0 && v !== '0' && v !== false;
const CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
interface PaintedZoneNotice { key: string; reason: string; name: string }
// A broken held input is a wall diagnostic, not work owed by every creature each round.
// Publish only from a real tick; synthetic preflights must not consume the notice.
const paintedNotices = new Set<string>();
const paintedEndZone = zoneIsPainted;
export interface ZoneEndClaim { zone_status_id: string; caster_id: string; target_id: string; item_id: string; already_resolved: boolean }
export interface ZoneRadiusChange {
  kind: 'class_aura_radius'; status_id: string; before: number; status_aoe_distance: number;
}
export interface ZoneEndPlan {
  kind: 'plan';
  dispatches: Array<{ zoneStatusId: string; casterId: string; abilityId: string; connectedId: string; forcedExtreme: number }>;
  strips: Array<{ status_id: string; status_item_id: string; target: string; caster: string; before: Row }>;
  claims: ZoneEndClaim[];
  radiusChanges?: ZoneRadiusChange[];
  paintedNotices?: PaintedZoneNotice[];
}
export type ZoneEndDecision = ZoneEndPlan | { kind: 'clear' } | { kind: 'handback'; reason: string };
export interface ZoneEndReaders extends ZoneReaders {
  ability: JanusCtx['read'];
  combatSizeFt?: WalkZoneReaders['combatSizeFt'];
  // A host that holds a saved-roll answer can skip it. The page posts false when
  // it lacks that answer; the commit compares the claim under the encounter lock.
  alreadyResolved?: (claim: Omit<ZoneEndClaim, 'already_resolved'>) => boolean;
}
export function planEndOfTurnZones(encounter: Row, creature: Row, shape: Row | null, read: ZoneEndReaders): ZoneEndDecision {
  try {
    // The lean blob elides a self-caster key. Hydrate exactly as the shared page
    // status reader does, while preserving an explicitly empty caster.
    const hydrate = (s: Row): Row => 'status_caster_id' in s ? s : { ...s, status_caster_id: s.status_target_id };
    const id = ecId(creature), abilityRead = read.ability;
    const auraRead: WalkZoneReaders = { ...read, definition: abilityRead.definition,
      has: abilityRead.has, layer: abilityRead.value, combatSizeFt: row => read.combatSizeFt?.(row) };
    // Every OTHER carrier's aura is judged at its refreshed radius too, as the walk judges it (genotele): its stored copy waits for that
    // carrier's own End Turn. Uncomputable → its stored radius, as before.
    const roster = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : []).filter((r): r is Row => !!record(r));
    const zones = refreshClassAuraZones(collectPersistentZones(encounter, read), roster, auraRead, { lenient: true, skip: id })
      .map(z => ({ ...z, status: hydrate(z.status) }));
    if (!zones.length) return { kind: 'clear' };
    const plan: ZoneEndPlan = { kind: 'plan', dispatches: [], strips: [], claims: [] };
    const anchor = classAuraActiveAnchor(id, abilityRead.statuses(creature).map(hydrate),
      (def, key) => abilityRead.value(record(def), key), abilityRead.definition);
    if (anchor) {
      const def = record(anchor.status_arr) ?? abilityRead.definition(statusItemId(anchor));
      if (!def) throw new ZoneHandBack('persistent-zone:class-aura-definition');
      const expected = classAuraExpectedStored(def, creature, auraRead);
      if (expected === undefined) throw new ZoneHandBack('persistent-zone:class-aura-size');
      const before = Number(statusInputSnapshot(anchor).status_aoe_distance);
      if (Math.abs(before - expected) >= 0.001) {
        const change: ZoneRadiusChange = { kind: 'class_aura_radius', status_id: upper(anchor.status_id), before, status_aoe_distance: expected };
        plan.radiusChanges = [change];
        // Judge the refreshed radius without mutating the page's before-image.
        for (const zone of zones) if (upper(zone.status.status_id) === change.status_id)
          zone.status = { ...zone.status, status_aoe_distance: expected };
      }
    }
    const ordinary = zones.filter(z => !paintedEndZone(z));
    const inside = new Set(ordinary.length ? intersectingZoneKeys(creature, ordinary, shape, read) : []);
    const unavailable = new Set<string>();
    for (const zone of zones.filter(paintedEndZone)) {
      try {
        if (!paintedZoneSnapshot(zone)) throw new ZoneHandBack('persistent-zone:painted-geometry');
        for (const key of intersectingZoneKeys(creature, [zone], shape, read)) inside.add(key);
      } catch (error) {
        if (!(error instanceof ZoneHandBack)) throw error;
        unavailable.add(zone.key);
        const definition = abilityRead.definition(upper(zone.status.status_origin_id) || upper(zone.status.status_item_id));
        const name = String(itemField(definition, 'item_name') ?? 'Painted wall');
        (plan.paintedNotices ??= []).push({ key: `${zone.key}|${error.reason}`, reason: error.reason, name });
      }
    }
    const pair = (s: Row): string => `${upper(s.status_origin_id) || upper(s.status_item_id)}|${upper(s.status_caster_id)}`;
    const occupiedPairs = new Set(zones.filter(z => inside.has(z.key)).map(z => pair(z.status)));
    const processed = new Set<string>(), stripped = new Set<string>();
    for (const zone of zones) {
      if (unavailable.has(zone.key)) continue;
      const s = zone.status, origin = upper(s.status_origin_id) || upper(s.status_item_id);
      const selfOrigin = isSelfStatusOrigin(upper(s.status_id), upper(s.status_origin_id));
      const definition = selfOrigin ? null : abilityRead.definition(origin);
      if (!inside.has(zone.key)) {
        const connection = upper(s.status_connected_id);
        if (!connection && occupiedPairs.has(pair(s))) continue;
        for (const status of statusRows(creature, 'target').map(hydrate)) {
          const sid = upper(status.status_id), item = statusItemId(status);
          const tied = connection ? upper(status.status_connected_id) === connection
            : origin !== '' && upper(s.status_caster_id) !== '' && upper(status.status_caster_id) === upper(s.status_caster_id)
              && [upper(status.status_origin_id), item, upper(itemField(status, 'item_id'))].includes(origin);
          if (!tied || item === CONCENTRATING || !sid || stripped.has(sid)) continue;
          stripped.add(sid);
          plan.strips.push({ status_id: sid, status_item_id: item, target: id, caster: upper(status.status_caster_id), before: statusInputSnapshot(status) });
        }
        continue;
      }
      if (processed.has(zone.key)) continue;
      processed.add(zone.key);
      // Dispatch uses PHP's null-coalescing origin; reconcile above deliberately
      // uses its separate empty-string fallback.
      const dispatchOrigin = upper(s.status_origin_id ?? s.status_item_id ?? '');
      const casterId = upper(s.status_caster_id);
      if (!dispatchOrigin || selfOrigin) continue;
      if (!/^[0-9A-F-]{36}$/.test(casterId) || !/^[0-9A-F-]{36}$/.test(origin)) continue;
      if (!definition) return { kind: 'handback', reason: 'zone-definition-not-held' };
      if (itemField(definition, 'item_type') === 'terrain_type') continue;
      // A painted wall's end-turn payload is its authored roll. Barrier/terrain-only
      // walls (Water, Force, Stone) must not reapply the spell as a no-roll status.
      if (zoneIsPainted(zone) && !abilityRead.has(definition, 'roll_type')) continue;
      const caster = (encounter.encounter_creatures_arr as Row[]).find(r => ecId(r) === casterId);
      if (!caster) return { kind: 'handback', reason: 'zone-caster-not-held' };
      const opposing = checkOpposingTeam({ read: abilityRead } as JanusCtx, caster, creature);
      // status_opposing_team_only = 2 (Wizard v9 aoe_spells_spare_allies): skip the zone's caster and the caster's team, never neutrals
      const skipSameTeam = Number(s.status_opposing_team_only ?? 0) === ZONE_GATE_SKIP_SAME_TEAM;
      if (skipSameTeam && sameTeamOrSelf(caster, creature, (row, key) => abilityRead.value(row, key))) continue;
      if (((!skipSameTeam && truthy(s.status_opposing_team_only)) || truthy(abilityRead.value(definition, 'effect_only_affects_opposing_team_type'))) && !opposing) continue;
      if (truthy(abilityRead.value(definition, 'effect_only_affects_same_team_type')) && opposing) continue;
      const adverse = adverseIds.has(upper(abilityRead.value(definition, 'roll_type') ?? (definition.roll_type_1 as unknown[])?.[0]));
      // check_item_arr_aura_effect: persistent + follows target (no Self range requirement).
      const aura = ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(k => abilityRead.value(definition, k) != null);
      if (adverse && casterId === id && aura) continue;
      if (adverse) {
        const claim = { zone_status_id: upper(s.status_id), caster_id: casterId, target_id: id, item_id: origin };
        const already = read.alreadyResolved?.(claim) ?? false;
        plan.claims.push({ ...claim, already_resolved: already });
        if (already) continue;
      }
      const connectedId = upper(s.status_connected_id);
      if (!/^[0-9A-F-]{36}$/.test(connectedId)) return { kind: 'handback', reason: 'zone-connection' };
      plan.dispatches.push({ zoneStatusId: upper(s.status_id), casterId, abilityId: origin, connectedId,
        forcedExtreme: Math.sign(Number(s.status_forced_roll_extreme ?? 0)) });
    }
    return plan;
  } catch (error) {
    return { kind: 'handback', reason: error instanceof ZoneHandBack ? error.reason : 'zone-judge-failed' };
  }
}

/** Patch existing target/caster copies, shared by tick clones and observer delivery. */
export function applyZoneRadiusChanges(holders: Iterable<Row>, changes: readonly ZoneRadiusChange[]): void {
  if (!changes.length) return;
  for (const holder of holders) for (const mode of ['target', 'caster'] as const) for (const status of statusRows(holder, mode)) {
    const change = changes.find(c => c.status_id === upper(status.status_id));
    if (change) {
      if (change.status_aoe_distance === 0) delete status.status_aoe_distance;
      else status.status_aoe_distance = change.status_aoe_distance;
    }
  }
}

/** Zone hooks share the tick's prepared browser context and physical row resources. */
export function reconcileEndOfTurnZoneExits(ctx: JanusCtx, ending: Row, plan?: ZoneEndPlan): void {
  if (!plan) return;
  for (const strip of plan.strips) {
    // Row-id removal is precise; broad item removal would eat another zone's grant.
    const definition = ctx.read.definition(strip.status_item_id);
    if (!definition) handBack('persistent-aoe-present:strip-definition-not-held');
    const lossEvent = { trigger: 'triggers_when_caster_loses_status_effect', statusItemId: strip.status_item_id };
    const lossArm = carrierHasTrigger(ctx, ending, lossEvent.trigger, 'persistent-aoe-present:exit-lifecycle', strip.status_item_id);
    if (lossArm) triggerFamilyPreDraw(ctx, ending, lossEvent);
    // Special removal cascades need their own handler; the plain zone grant is a row cull.
    if (['summon_creature', 'concentrating', 'effect_ends_on_caster_loses_status_effect', 'ability_charges', 'object_destroyed_after_duration_complete'].some(k => ctx.read.has(definition, k)))
      handBack('persistent-aoe-present:exit-lifecycle');
    if (/^(?:hasted|riding|ridden|piloting|piloted|rotting|spoiling|grappled|grappling|restrained|resting)$/.test(String(itemField(definition, 'item_name') ?? '').toLowerCase())
      || itemField(definition, 'item_type') === 'creature') handBack('persistent-aoe-present:exit-lifecycle');
    for (const bucket of ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'lair_ability', 'trap_ability',
      'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta', 'sanguine', 'ability_choice']) {
      const pool = [...ctx.read.entries(definition, bucket), ...(Array.isArray(definition[bucket]) ? definition[bucket] as unknown[] : [])];
      for (const entry of pool) {
        const id = upper(typeof entry === 'string' ? entry.split('|')[0] : Array.isArray(entry) ? entry[0] : itemField(record(entry), 'item_id'));
        if (!id) continue;
        const nested = ctx.read.definition(id);
        if (!nested || ctx.read.has(nested, 'ability_charges')) handBack('persistent-aoe-present:exit-lifecycle');
      }
    }
    const skillCluster = record(record(record(ending.computed)?.creature)?.skills) ?? {};
    if (Object.values(skillCluster).some(entry => {
      const lines = record(record(entry)?.adv_dis_breakdown)?.lines;
      return Array.isArray(lines) && lines.some(line => upper(record(line)?.href_id) === strip.status_id);
    })) handBack('persistent-aoe-present:exit-skill-projection');
    ctx.statusesRemoved.push(strip);
    for (const member of ctx.members.values()) removeStatusFromRow(member, strip.status_id);
    if (lossArm) runTriggerFamily(ctx, lossEvent, ending, null, [ending], false, undefined, true);
    // PHP's exact-row exit removal writes no log row.
  }
}
export function runEndOfTurnZones(ctx: JanusCtx, ending: Row, plan?: ZoneEndPlan): void {
  if (!plan) return;
  if (!ctx.dryRun) for (const notice of plan.paintedNotices ?? []) {
    if (paintedNotices.has(notice.key)) continue;
    if (paintedNotices.size >= 128) paintedNotices.delete(paintedNotices.values().next().value!);
    paintedNotices.add(notice.key);
    refuseUnported(notice.reason, { boundary: 'zone-end', wall: notice.name }, `${notice.name}: ${notice.reason}`);
  }
  for (const step of plan.dispatches) {
    const caster = ctx.members.get(step.casterId), definition = ctx.read.definition(step.abilityId);
    if (!caster || !definition) handBack('persistent-aoe-present:zone-input-not-held');
    const rollType = upper(ctx.read.value(definition, 'roll_type'));
    const contest = rollType === 'AD80D31D-898B-66CE-0894-1335D11B1529'
      || (rollType === '77A044B7-7041-CA96-3C63-BB718B15A364' && ctx.read.has(definition, 'skill_roll_contestee'));
    // The legacy saved-roll lookup is repeated for EACH anchor. A prior contact
    // (or target tick) in this same boundary may now satisfy it; an immunity skip
    // that produced no qualifying row must not suppress a later dispatch.
    if (adverseIds.has(rollType) && ctx.rows.some(row => upper(row.roll_item_id) === step.abilityId
      && upper(row.roll_by) === (contest ? ecId(ending) : step.casterId)
      && upper(row.roll_to) === (contest ? step.casterId : ecId(ending)))) continue;
    const priorRead = ctx.read, priorConnection = ctx.statusConnectedId, priorStart = ctx.dispatchRowStart, priorStatuses = ctx.priorDispatchStatuses;
    const priorExtremes = ctx.castForcedRollExtreme;
    ctx.castForcedRollExtreme = {};
    ctx.statusConnectedId = step.connectedId;
    ctx.dispatchRowStart = ctx.rows.length;
    ctx.priorDispatchStatuses = new Set(ctx.statusesAdded);
    if (step.forcedExtreme) {
      const tick = upper(priorRead.value(definition, 'roll_type')) === '77A044B7-7041-CA96-3C63-BB718B15A364'
        ? '77A044B7-7041-CA96-3C63-BB718B15A364' : '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';
      const side = step.forcedExtreme > 0 ? 'roll_type_is_rolled_at_max' : 'roll_type_is_rolled_at_min';
      ctx.read = { ...priorRead, entries: (row, key) => {
        const list = priorRead.entries(row, key);
        if (!row || ecId(row) !== step.casterId || !['roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min'].includes(key)) return list;
        const kept = list.filter(e => upper(typeof e === 'string' ? e.split('|')[0] : Array.isArray(e) ? e[0] : itemField(record(e), 'item_id')) !== tick);
        return key === side ? [...kept, tick] : kept;
      } };
    }
    try {
      janus(ctx, { casterId: step.casterId, targetIds: [ecId(ending)], abilityId: step.abilityId, rawDiceMod: 0,
        rawDiceAdvDisStacks: 0, useAbilityVariable1: PAOE_END, useAbilityVariable2: null, castingTargets: ecId(ending) },
        JSON.parse(JSON.stringify(definition)) as Row, caster, [ending]);
      for (const row of ctx.rows.slice(ctx.dispatchRowStart)) {
        if (row.random_table || row.house_rule) handBack('persistent-aoe-present:zone-log-metadata');
        row.roll_private = 0;
      }
      if (ctx.inventoryChanged.length || ctx.playerCharactersChanged?.length) handBack('persistent-aoe-present:zone-write-channel');
    } catch (error) {
      if (error instanceof HandBack && /(?:creature|pc)-death-not-modeled|lethal-no-death-tail/.test(error.reason)) handBack('turn-tick:zone-eot-may-kill');
      throw error;
    } finally {
      ctx.read = priorRead; ctx.statusConnectedId = priorConnection; ctx.dispatchRowStart = priorStart; ctx.priorDispatchStatuses = priorStatuses;
      ctx.castForcedRollExtreme = priorExtremes;
    }
  }
}
